import { T } from '../styles/tokens';
import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../api/client';
import { SkeletonBar, Button, Input, Select } from '../components/ui';

// ══════════════════════════════════════════════════════════════════════════
// TYPES
// ══════════════════════════════════════════════════════════════════════════

interface EmailConfig {
  id?: string; sendgrid_api_key?: string; from_email?: string; from_name?: string;
  daily_limit?: number; is_verified?: number; status?: string;
}

interface EmailSent {
  id: string; to_email: string; to_name: string | null; subject: string;
  status: string; sendgrid_message_id: string | null;
  created_at: string; opened_at: string | null; clicked_at: string | null;
}

interface EmailTemplate {
  id: string; name: string; subject: string; body_html: string; body_text: string | null;
  category: string; variables: string | null; is_active: number; created_at: string;
}

interface Campaign {
  id: string; name: string; subject: string; status: string; target_type: string;
  total_recipients: number; total_sent: number; total_opened: number;
  total_clicked: number; total_bounced: number; created_at: string; sent_at: string | null;
}

interface Tag {
  id: string; name: string; color: string; description: string | null;
  user_count: number; created_at: string;
}

interface Metrics {
  total_sent: number; total_delivered: number; total_opened: number; total_clicked: number;
  total_bounced: number; total_spam: number; total_failed: number;
  delivery_rate: number; open_rate: number; click_rate: number;
  bounce_rate: number; spam_rate: number;
}

interface PushConfig {
  id?: string; provider?: string; api_key?: string; app_id?: string; is_active?: number;
}

// ══════════════════════════════════════════════════════════════════════════
// STATE
// ══════════════════════════════════════════════════════════════════════════

const activeTab = signal<'email' | 'push'>('email');
const emailSubTab = signal<'config' | 'metrics' | 'sent' | 'templates' | 'campaigns' | 'tags'>('config');
const loading = signal(true);
const saving = signal(false);
const successMsg = signal('');
const errorMsg = signal('');

// Data
const emailConfig = signal<EmailConfig>({});
const metrics = signal<Metrics | null>(null);
const sentEmails = signal<EmailSent[]>([]);
const sentTotal = signal(0);
const sentPage = signal(0);
const templates = signal<EmailTemplate[]>([]);
const campaigns = signal<Campaign[]>([]);
const tags = signal<Tag[]>([]);
const pushConfig = signal<PushConfig>({});

// Forms
const configForm = signal<EmailConfig>({});
const pushForm = signal<PushConfig>({});
const sendForm = signal({ to_email: '', to_name: '', subject: '', body_html: '' });
const templateForm = signal({ name: '', subject: '', body_html: '', category: 'transactional' });
const campaignForm = signal({ name: '', subject: '', body_html: '', target_type: 'all' as string, target_emails: '' });
const tagForm = signal({ name: '', color: T.accent, description: '' });
const showSendForm = signal(false);
const showTemplateForm = signal(false);
const showCampaignForm = signal(false);
const showTagForm = signal(false);
const tagUsersForm = signal({ tagId: '', emails: '' });
const showTagUsersForm = signal(false);

// ══════════════════════════════════════════════════════════════════════════
// API
// ══════════════════════════════════════════════════════════════════════════

async function loadAll() {
  loading.value = true;
  try {
    const [cfgRes, metricsRes, sentRes, tplRes, campRes, tagsRes, pushRes] = await Promise.allSettled([
      api.get('/admin/cloud/crm/config'),
      api.get('/admin/cloud/crm/metrics'),
      api.get('/admin/cloud/crm/emails?limit=50&offset=0'),
      api.get('/admin/cloud/crm/templates'),
      api.get('/admin/cloud/crm/campaigns'),
      api.get('/admin/cloud/crm/tags'),
      api.get('/admin/cloud/crm/push/config'),
    ]);
    if (cfgRes.status === 'fulfilled') { const d = (cfgRes.value as any)?.data || cfgRes.value; emailConfig.value = d; configForm.value = { ...d }; }
    if (metricsRes.status === 'fulfilled') { metrics.value = (metricsRes.value as any)?.data || metricsRes.value as any; }
    if (sentRes.status === 'fulfilled') { const d = (sentRes.value as any)?.data || sentRes.value as any; sentEmails.value = d.emails || []; sentTotal.value = d.total || 0; }
    if (tplRes.status === 'fulfilled') { templates.value = ((tplRes.value as any)?.data || tplRes.value) as any[]; }
    if (campRes.status === 'fulfilled') { campaigns.value = ((campRes.value as any)?.data || campRes.value) as any[]; }
    if (tagsRes.status === 'fulfilled') { tags.value = ((tagsRes.value as any)?.data || tagsRes.value) as any[]; }
    if (pushRes.status === 'fulfilled') { const d = (pushRes.value as any)?.data || pushRes.value; pushConfig.value = d; pushForm.value = { ...d }; }
  } catch (_) {}
  loading.value = false;
}

function flash(msg: string, isError = false) {
  if (isError) { errorMsg.value = msg; } else { successMsg.value = msg; }
  setTimeout(() => { successMsg.value = ''; errorMsg.value = ''; }, 3000);
}

// ══════════════════════════════════════════════════════════════════════════
// STYLES (matching existing dark theme)
// ══════════════════════════════════════════════════════════════════════════

const card: any = {
  background: T.bgCard, border: `1px solid ${T.border}`,
  borderRadius: 14, padding: 20, marginBottom: 16,
};

const btnPrimary: any = {
  background: 'linear-gradient(135deg, #6366f1, T.purple)', color: T.text,
  border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer',
  fontWeight: 600, fontSize: 14,
};

const btnSecondary: any = {
  background: T.border, color: T.textSecondary, border: `1px solid ${T.borderHover}`,
  borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontSize: 14,
};

const btnDanger: any = {
  background: T.dangerBg, color: T.danger, border: `1px solid ${T.dangerBg}`,
  borderRadius: 10, padding: '8px 16px', cursor: 'pointer', fontSize: 13,
};

const inputStyle: any = {
  width: '100%', padding: '10px 14px', background: T.bgCardHover,
  border: `1px solid ${T.borderHover}`, borderRadius: 10, color: T.text,
  fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

const labelStyle: any = { fontSize: 13, color: T.textSecondary, marginBottom: 4, display: 'block', fontWeight: 500 };

const tabStyle = (active: boolean): any => ({
  padding: '10px 20px', cursor: 'pointer', fontSize: 14, fontWeight: active ? 600 : 500,
  color: active ? '#fff' : T.textMuted, borderBottom: active ? '2px solid #6366f1' : '2px solid transparent',
  background: 'transparent', border: 'none', borderBottomStyle: 'solid',
  transition: 'all 0.2s',
});

const badge = (color: string): any => ({
  display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
  background: `${color}22`, color, border: `1px solid ${color}44`,
});

const statusColor: Record<string, string> = {
  sent: T.success, delivered: T.success, opened: T.primary, clicked: T.purple,
  bounced: T.danger, spam: T.warning, failed: T.danger, queued: T.textMuted,
  draft: T.textMuted, sending: T.warning, cancelled: T.danger,
  active: T.success, inactive: T.textMuted, pending: T.warning,
};

// ══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════════════════════════════════════════

export function CRMPage() {
  useEffect(() => { loadAll(); }, []);

  if (loading.value) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, margin: 0 }}>
 CRM
        </h1>
        <p style={{ color: T.textMuted, fontSize: 14, marginTop: 4 }}>
          Email marketing, campanhas e notificações push
        </p>
      </div>

      {/* Toast */}
      {successMsg.value && (
        <div style={{ ...card, background: T.successBg, border: `1px solid ${T.successBgStrong}`, color: T.success, marginBottom: 16 }}>
 {successMsg.value}
        </div>
      )}
      {errorMsg.value && (
        <div style={{ ...card, background: T.dangerBg, border: `1px solid ${T.dangerBg}`, color: T.danger, marginBottom: 16 }}>
 {errorMsg.value}
        </div>
      )}

      {/* Main Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${T.bgHover}`, marginBottom: 24 }}>
 <Button variant="ghost" style={tabStyle(activeTab.value === 'email')} onClick={() => { activeTab.value = 'email'; }}>E-mail</Button>
 <Button variant="ghost" style={tabStyle(activeTab.value === 'push')} onClick={() => { activeTab.value = 'push'; }}>Push</Button>
      </div>

      {activeTab.value === 'email' && <EmailTab />}
      {activeTab.value === 'push' && <PushTab />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// EMAIL TAB
// ══════════════════════════════════════════════════════════════════════════

function EmailTab() {
  return (
    <div>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 20 }}>
        {(['config', 'metrics', 'sent', 'templates', 'campaigns', 'tags'] as const).map(t => (
          <Button variant="ghost"
            key={t}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
              background: emailSubTab.value === t ? T.accentBg : T.bgCard,
              color: emailSubTab.value === t ? T.accent : T.textSecondary,
            }}
            onClick={() => { emailSubTab.value = t; }}
          >
 {t === 'config' && 'Config'}
 {t === 'metrics' && 'Métricas'}
 {t === 'sent' && 'Enviados'}
 {t === 'templates' && 'Templates'}
 {t === 'campaigns' && 'Campanhas'}
 {t === 'tags' && 'Tags'}
          </Button>
        ))}
      </div>

      {emailSubTab.value === 'config' && <ConfigSection />}
      {emailSubTab.value === 'metrics' && <MetricsSection />}
      {emailSubTab.value === 'sent' && <SentSection />}
      {emailSubTab.value === 'templates' && <TemplatesSection />}
      {emailSubTab.value === 'campaigns' && <CampaignsSection />}
      {emailSubTab.value === 'tags' && <TagsSection />}
    </div>
  );
}

// ─── Config Section ──────────────────────────────────────────────────

function ConfigSection() {
  const save = async () => {
    saving.value = true;
    try {
      await api.post('/admin/cloud/crm/config', configForm.value);
      flash('Configuração salva!');
      await loadAll();
    } catch (e) { flash('Erro ao salvar', true); }
    saving.value = false;
  };

  return (
    <div style={card}>
 <h3 style={{ color: T.text, fontSize: 18, marginTop: 0, marginBottom: 16 }}>Configuração SendGrid</h3>
      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <div>
          <label style={labelStyle}>API Key SendGrid</label>
          <input
            type="password"
            style={inputStyle}
            placeholder="SG.xxxxx..."
            value={configForm.value.sendgrid_api_key || ''}
            onInput={(e: any) => { configForm.value = { ...configForm.value, sendgrid_api_key: e.target.value }; }}
          />
        </div>
        <div>
          <label style={labelStyle}>Email Remetente</label>
          <input
            type="email"
            style={inputStyle}
            placeholder="noreply@seusite.com"
            value={configForm.value.from_email || ''}
            onInput={(e: any) => { configForm.value = { ...configForm.value, from_email: e.target.value }; }}
          />
        </div>
        <div>
          <label style={labelStyle}>Nome Remetente</label>
          <input
            style={inputStyle}
            placeholder="Minha Empresa"
            value={configForm.value.from_name || ''}
            onInput={(e: any) => { configForm.value = { ...configForm.value, from_name: e.target.value }; }}
          />
        </div>
        <div>
          <label style={labelStyle}>Limite Diário</label>
          <input
            type="number"
            style={inputStyle}
            value={configForm.value.daily_limit || 500}
            onInput={(e: any) => { configForm.value = { ...configForm.value, daily_limit: parseInt(e.target.value) }; }}
          />
        </div>
      </div>

      {emailConfig.value.id && (
        <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={badge(statusColor[emailConfig.value.status || 'inactive'] || T.textMuted)}>
            {emailConfig.value.status || 'inactive'}
          </span>
          {emailConfig.value.is_verified ? (
 <span style={{ color: T.success, fontSize: 13 }}>Verificado</span>
          ) : (
 <span style={{ color: T.warning, fontSize: 13 }}>Não verificado</span>
          )}
        </div>
      )}

      <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
        <Button variant="primary" onClick={save} disabled={saving.value}>
 {saving.value ? 'Salvando...' : 'Salvar Config'}
        </Button>
      </div>
    </div>
  );
}

// ─── Metrics Section ─────────────────────────────────────────────────

function MetricsSection() {
  const m = metrics.value;
  if (!m) return <div style={card}><p style={{ color: T.textMuted }}>Nenhuma métrica disponível ainda.</p></div>;

  const MetricCard = ({ label, value, suffix, color }: { label: string; value: number; suffix?: string; color: string }) => (
    <div style={{ ...card, textAlign: 'center', padding: 20 }}>
      <div style={{ fontSize: 32, fontWeight: 700, color }}>{typeof value === 'number' && value % 1 !== 0 ? value.toFixed(1) : value}{suffix}</div>
      <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>{label}</div>
    </div>
  );

  return (
    <div>
 <h3 style={{ color: T.text, fontSize: 18, marginBottom: 16 }}>Saúde do Email</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <MetricCard label="Total Enviados" value={m.total_sent} color="#fff" />
        <MetricCard label="Entregues" value={m.delivery_rate} suffix="%" color={T.success} />
        <MetricCard label="Taxa de Abertura" value={m.open_rate} suffix="%" color={T.primary} />
        <MetricCard label="Taxa de Clique" value={m.click_rate} suffix="%" color={T.purple} />
        <MetricCard label="Bounce Rate" value={m.bounce_rate} suffix="%" color={T.danger} />
        <MetricCard label="Spam Rate" value={m.spam_rate} suffix="%" color={T.warning} />
      </div>

      <div style={{ ...card, marginTop: 16 }}>
 <h4 style={{ color: T.text, margin: '0 0 12px' }}>Resumo</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
          <div><span style={{ color: T.textMuted, fontSize: 13 }}>Enviados:</span> <span style={{ color: T.text }}>{m.total_sent}</span></div>
          <div><span style={{ color: T.textMuted, fontSize: 13 }}>Entregues:</span> <span style={{ color: T.success }}>{m.total_delivered}</span></div>
          <div><span style={{ color: T.textMuted, fontSize: 13 }}>Abertos:</span> <span style={{ color: T.primary }}>{m.total_opened}</span></div>
          <div><span style={{ color: T.textMuted, fontSize: 13 }}>Clicados:</span> <span style={{ color: T.purple }}>{m.total_clicked}</span></div>
          <div><span style={{ color: T.textMuted, fontSize: 13 }}>Bounced:</span> <span style={{ color: T.danger }}>{m.total_bounced}</span></div>
          <div><span style={{ color: T.textMuted, fontSize: 13 }}>Spam:</span> <span style={{ color: T.warning }}>{m.total_spam}</span></div>
          <div><span style={{ color: T.textMuted, fontSize: 13 }}>Falharam:</span> <span style={{ color: T.danger }}>{m.total_failed}</span></div>
        </div>
      </div>
    </div>
  );
}

// ─── Sent Emails Section ─────────────────────────────────────────────

function SentSection() {
  const send = async () => {
    saving.value = true;
    try {
      const f = sendForm.value;
      if (!f.to_email || !f.subject || !f.body_html) { flash('Preencha todos os campos', true); saving.value = false; return; }
      await api.post('/admin/cloud/crm/emails/send', f);
      flash('Email enviado!');
      sendForm.value = { to_email: '', to_name: '', subject: '', body_html: '' };
      showSendForm.value = false;
      await loadAll();
    } catch (e) { flash('Erro ao enviar email', true); }
    saving.value = false;
  };

  const loadPage = async (page: number) => {
    const offset = page * 50;
    try {
      const res = await api.get(`/admin/cloud/crm/emails?limit=50&offset=${offset}`) as any;
      const d = res?.data || res;
      sentEmails.value = d.emails || [];
      sentTotal.value = d.total || 0;
      sentPage.value = page;
    } catch (_) {}
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
 <h3 style={{ color: T.text, fontSize: 18, margin: 0 }}>Emails Enviados ({sentTotal.value})</h3>
        <Button variant="primary" onClick={() => { showSendForm.value = !showSendForm.value; }}>
 Enviar Email
        </Button>
      </div>

      {/* Send form */}
      {showSendForm.value && (
        <div style={{ ...card, border: `1px solid ${T.accentBgStrong}` }}>
 <h4 style={{ color: T.text, margin: '0 0 16px' }}>Enviar Email Individual</h4>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
            <div>
              <label style={labelStyle}>Para (email)</label>
              <input style={inputStyle} placeholder="user@email.com" value={sendForm.value.to_email}
                onInput={(e: any) => { sendForm.value = { ...sendForm.value, to_email: e.target.value }; }} />
            </div>
            <div>
              <label style={labelStyle}>Nome</label>
              <input style={inputStyle} placeholder="Nome do destinatário" value={sendForm.value.to_name}
                onInput={(e: any) => { sendForm.value = { ...sendForm.value, to_name: e.target.value }; }} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Assunto</label>
            <input style={inputStyle} placeholder="Assunto do email" value={sendForm.value.subject}
              onInput={(e: any) => { sendForm.value = { ...sendForm.value, subject: e.target.value }; }} />
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Conteúdo HTML</label>
            <Textarea style={{ ...inputStyle, minHeight: 120, fontFamily: 'monospace', resize: 'vertical' }} placeholder="<h1>Olá!</h1>" value={sendForm.value.body_html}
              onInput={(e: any) => { sendForm.value = { ...sendForm.value, body_html: e.target.value }; }} />
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
            <Button variant="primary" onClick={send} disabled={saving.value}>
 {saving.value ? 'Enviando...' : 'Enviar'}
            </Button>
            <Button variant="ghost" onClick={() => { showSendForm.value = false; }}>Cancelar</Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ ...card, padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.bgHover}` }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: T.textMuted, fontWeight: 500 }}>Para</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: T.textMuted, fontWeight: 500 }}>Assunto</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: T.textMuted, fontWeight: 500 }}>Status</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: T.textMuted, fontWeight: 500 }}>Data</th>
            </tr>
          </thead>
          <tbody>
            {sentEmails.value.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: T.textMuted }}>Nenhum email enviado ainda</td></tr>
            ) : sentEmails.value.map(e => (
              <tr key={e.id} style={{ borderBottom: `1px solid ${T.bgCard}` }}>
                <td style={{ padding: '10px 16px', color: T.text }}>{e.to_email}</td>
                <td style={{ padding: '10px 16px', color: T.textSecondary }}>{e.subject}</td>
                <td style={{ padding: '10px 16px' }}><span style={badge(statusColor[e.status] || T.textMuted)}>{e.status}</span></td>
                <td style={{ padding: '10px 16px', color: T.textMuted }}>{new Date(e.created_at).toLocaleString('pt-BR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {sentTotal.value > 50 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
          <Button variant="ghost" disabled={sentPage.value === 0} onClick={() => loadPage(sentPage.value - 1)}>← Anterior</Button>
          <span style={{ color: T.textMuted, padding: '10px 0', fontSize: 13 }}>Página {sentPage.value + 1} de {Math.ceil(sentTotal.value / 50)}</span>
          <Button variant="ghost" disabled={(sentPage.value + 1) * 50 >= sentTotal.value} onClick={() => loadPage(sentPage.value + 1)}>Próxima →</Button>
        </div>
      )}
    </div>
  );
}

// ─── Templates Section ───────────────────────────────────────────────

function TemplatesSection() {
  const create = async () => {
    saving.value = true;
    try {
      const f = templateForm.value;
      if (!f.name || !f.subject || !f.body_html) { flash('Preencha todos os campos', true); saving.value = false; return; }
      await api.post('/admin/cloud/crm/templates', f);
      flash('Template criado!');
      templateForm.value = { name: '', subject: '', body_html: '', category: 'transactional' };
      showTemplateForm.value = false;
      await loadAll();
    } catch (e) { flash('Erro ao criar template', true); }
    saving.value = false;
  };

  const del = async (id: string) => {
    if (!confirm('Deletar este template?')) return;
    try {
      await api.delete(`/admin/cloud/crm/templates/${id}`);
      flash('Template deletado');
      await loadAll();
    } catch (e) { flash('Erro ao deletar', true); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
 <h3 style={{ color: T.text, fontSize: 18, margin: 0 }}>Templates ({templates.value.length})</h3>
        <Button variant="primary" onClick={() => { showTemplateForm.value = !showTemplateForm.value; }}>+ Novo Template</Button>
      </div>

      {showTemplateForm.value && (
        <div style={{ ...card, border: `1px solid ${T.accentBgStrong}` }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={labelStyle}>Nome</label>
              <input style={inputStyle} placeholder="Welcome Email" value={templateForm.value.name}
                onInput={(e: any) => { templateForm.value = { ...templateForm.value, name: e.target.value }; }} />
            </div>
            <div>
              <label style={labelStyle}>Assunto</label>
              <input style={inputStyle} placeholder="Bem-vindo!" value={templateForm.value.subject}
                onInput={(e: any) => { templateForm.value = { ...templateForm.value, subject: e.target.value }; }} />
            </div>
            <div>
              <label style={labelStyle}>Categoria</label>
              <select style={inputStyle} value={templateForm.value.category}
                onChange={(e: any) => { templateForm.value = { ...templateForm.value, category: e.target.value }; }}>
                <option value="transactional">Transacional</option>
                <option value="marketing">Marketing</option>
                <option value="notification">Notificação</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>HTML</label>
              <Textarea style={{ ...inputStyle, minHeight: 120, fontFamily: 'monospace', resize: 'vertical' }} value={templateForm.value.body_html}
                onInput={(e: any) => { templateForm.value = { ...templateForm.value, body_html: e.target.value }; }} />
            </div>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
 <Button variant="primary" onClick={create} disabled={saving.value}>{saving.value ? '...' : 'Criar'}</Button>
            <Button variant="ghost" onClick={() => { showTemplateForm.value = false; }}>Cancelar</Button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {templates.value.length === 0 ? (
          <div style={card}><p style={{ color: T.textMuted, textAlign: 'center' }}>Nenhum template criado ainda</p></div>
        ) : templates.value.map(t => (
          <div key={t.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ color: T.text, fontWeight: 600, fontSize: 15 }}>{t.name}</div>
                <div style={{ color: T.textSecondary, fontSize: 13, marginTop: 4 }}>Assunto: {t.subject}</div>
                <div style={{ marginTop: 8 }}>
                  <span style={badge(t.category === 'transactional' ? T.primary : t.category === 'marketing' ? T.purple : T.success)}>
                    {t.category}
                  </span>
                </div>
              </div>
 <Button variant="danger" size="sm" onClick={() => del(t.id)}></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Campaigns Section ───────────────────────────────────────────────

function CampaignsSection() {
  const create = async () => {
    saving.value = true;
    try {
      const f = campaignForm.value;
      if (!f.name || !f.subject) { flash('Preencha nome e assunto', true); saving.value = false; return; }
      const payload: any = { name: f.name, subject: f.subject, body_html: f.body_html, target_type: f.target_type };
      if (f.target_type === 'specific' && f.target_emails) {
        payload.target_emails = f.target_emails.split(',').map((e: string) => e.trim()).filter(Boolean);
      }
      await api.post('/admin/cloud/crm/campaigns', payload);
      flash('Campanha criada!');
      campaignForm.value = { name: '', subject: '', body_html: '', target_type: 'all', target_emails: '' };
      showCampaignForm.value = false;
      await loadAll();
    } catch (e) { flash('Erro ao criar campanha', true); }
    saving.value = false;
  };

  const sendCamp = async (id: string) => {
    if (!confirm('Enviar esta campanha? Os emails serão disparados.')) return;
    try {
      await api.post(`/admin/cloud/crm/campaigns/${id}/send`, {});
      flash('Campanha enviada!');
      await loadAll();
    } catch (e) { flash('Erro ao enviar campanha', true); }
  };

  const del = async (id: string) => {
    if (!confirm('Deletar esta campanha?')) return;
    try {
      await api.delete(`/admin/cloud/crm/campaigns/${id}`);
      flash('Campanha deletada');
      await loadAll();
    } catch (e) { flash('Erro ao deletar', true); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
 <h3 style={{ color: T.text, fontSize: 18, margin: 0 }}>Campanhas ({campaigns.value.length})</h3>
        <Button variant="primary" onClick={() => { showCampaignForm.value = !showCampaignForm.value; }}>+ Nova Campanha</Button>
      </div>

      {showCampaignForm.value && (
        <div style={{ ...card, border: `1px solid ${T.accentBgStrong}` }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={labelStyle}>Nome da Campanha</label>
              <input style={inputStyle} placeholder="Black Friday 2026" value={campaignForm.value.name}
                onInput={(e: any) => { campaignForm.value = { ...campaignForm.value, name: e.target.value }; }} />
            </div>
            <div>
              <label style={labelStyle}>Assunto</label>
 <input style={inputStyle} placeholder="Oferta especial!" value={campaignForm.value.subject}
                onInput={(e: any) => { campaignForm.value = { ...campaignForm.value, subject: e.target.value }; }} />
            </div>
            <div>
              <label style={labelStyle}>Destino</label>
              <select style={inputStyle} value={campaignForm.value.target_type}
                onChange={(e: any) => { campaignForm.value = { ...campaignForm.value, target_type: e.target.value }; }}>
                <option value="all">Todos os contatos</option>
                <option value="tags">Por Tags</option>
                <option value="specific">Emails específicos</option>
              </select>
            </div>
            {campaignForm.value.target_type === 'specific' && (
              <div>
                <label style={labelStyle}>Emails (separados por vírgula)</label>
                <Textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                  placeholder="user1@email.com, user2@email.com"
                  value={campaignForm.value.target_emails}
                  onInput={(e: any) => { campaignForm.value = { ...campaignForm.value, target_emails: e.target.value }; }} />
              </div>
            )}
            <div>
              <label style={labelStyle}>Conteúdo HTML</label>
              <Textarea style={{ ...inputStyle, minHeight: 120, fontFamily: 'monospace', resize: 'vertical' }} value={campaignForm.value.body_html}
                onInput={(e: any) => { campaignForm.value = { ...campaignForm.value, body_html: e.target.value }; }} />
            </div>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
 <Button variant="primary" onClick={create} disabled={saving.value}>{saving.value ? '...' : 'Criar'}</Button>
            <Button variant="ghost" onClick={() => { showCampaignForm.value = false; }}>Cancelar</Button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {campaigns.value.length === 0 ? (
          <div style={card}><p style={{ color: T.textMuted, textAlign: 'center' }}>Nenhuma campanha criada ainda</p></div>
        ) : campaigns.value.map(c => (
          <div key={c.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: T.text, fontWeight: 600, fontSize: 15 }}>{c.name}</div>
                <div style={{ color: T.textSecondary, fontSize: 13, marginTop: 4 }}>Assunto: {c.subject}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <span style={badge(statusColor[c.status] || T.textMuted)}>{c.status}</span>
                  <span style={{ color: T.textMuted, fontSize: 12 }}>
 {c.total_sent}/{c.total_recipients} | {c.total_opened} | {c.total_clicked}
                  </span>
                </div>
                {c.sent_at && <div style={{ color: T.textMuted, fontSize: 12, marginTop: 4 }}>Enviado: {new Date(c.sent_at).toLocaleString('pt-BR')}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
 {c.status === 'draft' && <Button variant="primary" onClick={() => sendCamp(c.id)}>Enviar</Button>}
 <Button variant="danger" size="sm" onClick={() => del(c.id)}></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tags Section ────────────────────────────────────────────────────

function TagsSection() {
  const create = async () => {
    saving.value = true;
    try {
      const f = tagForm.value;
      if (!f.name) { flash('Nome é obrigatório', true); saving.value = false; return; }
      await api.post('/admin/cloud/crm/tags', f);
      flash('Tag criada!');
      tagForm.value = { name: '', color: T.accent, description: '' };
      showTagForm.value = false;
      await loadAll();
    } catch (e) { flash('Erro ao criar tag', true); }
    saving.value = false;
  };

  const del = async (id: string) => {
    if (!confirm('Deletar esta tag?')) return;
    try {
      await api.delete(`/admin/cloud/crm/tags/${id}`);
      flash('Tag deletada');
      await loadAll();
    } catch (e) { flash('Erro ao deletar', true); }
  };

  const assignUsers = async () => {
    saving.value = true;
    try {
      const users = tagUsersForm.value.emails.split(',').map(e => ({ email: e.trim() })).filter(u => u.email);
      if (!users.length) { flash('Adicione pelo menos um email', true); saving.value = false; return; }
      await api.post(`/admin/cloud/crm/tags/${tagUsersForm.value.tagId}/users`, { users });
      flash('Usuários adicionados!');
      showTagUsersForm.value = false;
      tagUsersForm.value = { tagId: '', emails: '' };
      await loadAll();
    } catch (e) { flash('Erro ao adicionar usuários', true); }
    saving.value = false;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
 <h3 style={{ color: T.text, fontSize: 18, margin: 0 }}>Tags ({tags.value.length})</h3>
        <Button variant="primary" onClick={() => { showTagForm.value = !showTagForm.value; }}>+ Nova Tag</Button>
      </div>

      {showTagForm.value && (
        <div style={{ ...card, border: `1px solid ${T.accentBgStrong}` }}>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 120px' }}>
            <div>
              <label style={labelStyle}>Nome</label>
              <input style={inputStyle} placeholder="VIP" value={tagForm.value.name}
                onInput={(e: any) => { tagForm.value = { ...tagForm.value, name: e.target.value }; }} />
            </div>
            <div>
              <label style={labelStyle}>Cor</label>
              <input type="color" style={{ ...inputStyle, padding: 4, height: 42 }} value={tagForm.value.color}
                onInput={(e: any) => { tagForm.value = { ...tagForm.value, color: e.target.value }; }} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Descrição</label>
            <input style={inputStyle} placeholder="Clientes VIP" value={tagForm.value.description}
              onInput={(e: any) => { tagForm.value = { ...tagForm.value, description: e.target.value }; }} />
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
 <Button variant="primary" onClick={create} disabled={saving.value}>{saving.value ? '...' : 'Criar'}</Button>
            <Button variant="ghost" onClick={() => { showTagForm.value = false; }}>Cancelar</Button>
          </div>
        </div>
      )}

      {showTagUsersForm.value && (
        <div style={{ ...card, border: `1px solid ${T.successBgStrong}` }}>
 <h4 style={{ color: T.text, margin: '0 0 12px' }}>Adicionar Usuários à Tag</h4>
          <div>
            <label style={labelStyle}>Emails (separados por vírgula)</label>
            <Textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
              placeholder="user1@email.com, user2@email.com"
              value={tagUsersForm.value.emails}
              onInput={(e: any) => { tagUsersForm.value = { ...tagUsersForm.value, emails: e.target.value }; }} />
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
 <Button variant="primary" onClick={assignUsers} disabled={saving.value}>{saving.value ? '...' : 'Adicionar'}</Button>
            <Button variant="ghost" onClick={() => { showTagUsersForm.value = false; }}>Cancelar</Button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {tags.value.length === 0 ? (
          <div style={card}><p style={{ color: T.textMuted, textAlign: 'center' }}>Nenhuma tag criada ainda</p></div>
        ) : tags.value.map(t => (
          <div key={t.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: t.color }} />
                <div>
                  <div style={{ color: T.text, fontWeight: 600 }}>{t.name}</div>
                  {t.description && <div style={{ color: T.textMuted, fontSize: 12 }}>{t.description}</div>}
                </div>
 <span style={{ color: T.textSecondary, fontSize: 12 }}> {t.user_count} usuários</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" onClick={() => {
                  tagUsersForm.value = { tagId: t.id, emails: '' };
                  showTagUsersForm.value = true;
                }}>+ Usuários</Button>
 <Button variant="danger" size="sm" onClick={() => del(t.id)}></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// PUSH TAB
// ══════════════════════════════════════════════════════════════════════════

function PushTab() {
  const save = async () => {
    saving.value = true;
    try {
      await api.post('/admin/cloud/crm/push/config', pushForm.value);
      flash('Config push salva!');
      await loadAll();
    } catch (e) { flash('Erro ao salvar', true); }
    saving.value = false;
  };

  return (
    <div>
      <div style={card}>
 <h3 style={{ color: T.text, fontSize: 18, marginTop: 0, marginBottom: 16 }}>Push Notifications</h3>
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <div>
            <label style={labelStyle}>Provider</label>
            <select style={inputStyle} value={pushForm.value.provider || 'firebase'}
              onChange={(e: any) => { pushForm.value = { ...pushForm.value, provider: e.target.value }; }}>
              <option value="firebase">Firebase Cloud Messaging</option>
              <option value="onesignal">OneSignal</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>API Key</label>
            <input type="password" style={inputStyle} placeholder="Chave API" value={pushForm.value.api_key || ''}
              onInput={(e: any) => { pushForm.value = { ...pushForm.value, api_key: e.target.value }; }} />
          </div>
          <div>
            <label style={labelStyle}>App ID</label>
            <input style={inputStyle} placeholder="ID do App" value={pushForm.value.app_id || ''}
              onInput={(e: any) => { pushForm.value = { ...pushForm.value, app_id: e.target.value }; }} />
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select style={inputStyle} value={pushForm.value.is_active ? '1' : '0'}
              onChange={(e: any) => { pushForm.value = { ...pushForm.value, is_active: parseInt(e.target.value) }; }}>
              <option value="0">Inativo</option>
              <option value="1">Ativo</option>
            </select>
          </div>
        </div>

        {pushConfig.value.id && (
          <div style={{ marginTop: 16 }}>
            <span style={badge(pushConfig.value.is_active ? T.success : T.textMuted)}>
              {pushConfig.value.is_active ? 'Ativo' : 'Inativo'}
            </span>
            <span style={{ color: T.textMuted, fontSize: 13, marginLeft: 12 }}>
              Provider: {pushConfig.value.provider || 'firebase'}
            </span>
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          <Button variant="primary" onClick={save} disabled={saving.value}>
 {saving.value ? 'Salvando...' : 'Salvar Config'}
          </Button>
        </div>
      </div>

      <div style={card}>
 <h4 style={{ color: T.text, margin: '0 0 12px' }}>Status</h4>
        <p style={{ color: T.textMuted, fontSize: 14 }}>
          {pushConfig.value.id
 ? `Push notifications configurado via ${pushConfig.value.provider || 'firebase'}. ${pushConfig.value.is_active ? 'Ativo e recebendo notificações.' : 'Inativo — ative para começar a enviar.'}`
            : 'Push notifications ainda não configurado. Preencha os dados acima para começar.'}
        </p>
      </div>
    </div>
  );
}
